import React, { useState } from 'react';
import { X, Repeat, DollarSign } from 'lucide-react';
import { usePOCManagement } from '@/hooks/admin/usePOCManagement';

export function SyncToAmplifyDialog({ poc, isOpen, onClose, onSuccess }) {
  const { syncToAmplify } = usePOCManagement();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [budget, setBudget] = useState(0);

  const handleSync = async () => {
    setIsSubmitting(true);
    try {
      await syncToAmplify(poc.id, budget);
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-md w-full m-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold flex items-center gap-2 text-gray-900 dark:text-white">
            <Repeat className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            Sync to Amplify
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><X className="h-6 w-6" /></button>
        </div>

        <div className="p-6 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200">{error}</div>}

          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <p className="text-sm text-gray-800 dark:text-gray-200">
              This will create an Amplify POC record for <strong>{poc.id}</strong> ({poc.poc_name}).
            </p>
          </div>

          <div>
            <label className="flex items-center gap-1 text-sm font-medium mb-2 dark:text-gray-300">
              <DollarSign className="h-4 w-4" />
              Traditional Budget
            </label>
            <input
              type="number"
              step="0.01"
              value={budget}
              onChange={(e) => setBudget(parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-gray-500 dark:bg-gray-800 dark:text-white"
              placeholder="0.00"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Enter the traditional budget to assign to this Amplify POC
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800" disabled={isSubmitting}>Cancel</button>
            <button onClick={handleSync} disabled={isSubmitting} className="px-4 py-2 text-sm text-white bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 rounded-lg disabled:opacity-50 flex items-center gap-2">
              <Repeat className="h-4 w-4" />
              {isSubmitting ? 'Syncing...' : 'Sync to Amplify'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SyncToAmplifyDialog;
